// Cookie Consent Manager - GDPR Compliance
import { DialogManager } from "../components/dialogs/dialog-manager";
import { I18nManager } from "./i18n-manager";

export interface CookieConsent {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
  timestamp: number;
}

export class CookieConsentManager {
  private i18nManager: I18nManager;
  private dialogManager: DialogManager;
  private consentKey = "cookie-consent";
  private currentConsent: CookieConsent | null = null;
  private isInitialized = false;
  private setupScheduled = false;

  constructor(i18nManager: I18nManager, dialogManager: DialogManager) {
    this.i18nManager = i18nManager;
    this.dialogManager = dialogManager;
  }

  init(): void {
    // Prevent duplicate initialization
    if (this.isInitialized) {
      return;
    }

    try {
      this.loadConsent();

      // Setup banner and policy links
      // Use multiple fallbacks to ensure it runs even in production
      const setup = () => {
        if (this.setupScheduled) {
          return; // Prevent duplicate setup calls
        }
        this.setupScheduled = true;
        this.setupCookieBanner();
        this.setupPolicyLinks();
        this.isInitialized = true;
      };

      // Try requestIdleCallback first, but with a shorter timeout
      if ("requestIdleCallback" in window) {
        requestIdleCallback(setup, { timeout: 500 });
        // Also set a hard timeout as backup in case requestIdleCallback doesn't fire
        setTimeout(setup, 2000);
      } else {
        // Fallback: use setTimeout
        setTimeout(setup, 100);
      }

      // Also try immediately if DOM is already ready (for production builds)
      if (
        document.readyState === "complete" ||
        document.readyState === "interactive"
      ) {
        // Use a small delay to ensure DOM is fully parsed
        setTimeout(setup, 50);
      }
    } catch (error) {
      console.error("Cookie consent manager initialization error:", error);
      // Don't throw - allow app to continue even if cookie consent fails
    }
  }

  private loadConsent(): void {
    try {
      const stored = localStorage.getItem(this.consentKey);
      if (stored) {
        this.currentConsent = JSON.parse(stored);
        this.applyConsent();
      }
    } catch (error) {
      console.error("Failed to load cookie consent:", error);
    }
  }

  private setupCookieBanner(): void {
    try {
      const banner = document.getElementById("cookie-banner");
      if (!banner) {
        // Retry after a short delay in case DOM isn't ready yet
        setTimeout(() => {
          const retryBanner = document.getElementById("cookie-banner");
          if (retryBanner) {
            this.setupBannerElement(retryBanner);
          } else {
            console.warn(
              "Cookie banner element not found after retry, skipping setup"
            );
          }
        }, 100);
        return;
      }

      this.setupBannerElement(banner);
    } catch (error) {
      console.error("Error setting up cookie banner:", error);
    }
  }

  private setupBannerElement(banner: HTMLElement): void {
    // Hide banner if consent already given
    if (this.currentConsent) {
      banner.classList.add("hidden");
      banner.classList.remove("show");
      return;
    }

    // Show banner if no consent (add show class to make it visible)
    banner.classList.remove("hidden");
    banner.classList.add("show");

    // Translate banner text
    const bannerText = banner.querySelector(".cookie-content p");
    if (bannerText) {
      bannerText.textContent =
        this.i18nManager.t("cookies.description") ||
        "We use cookies to enhance your experience and analyze our traffic. By continuing to use our site, you consent to our use of cookies.";
    }

    const acceptButton = banner.querySelector(".cookie-accept");
    const customizeButton = banner.querySelector(".cookie-customize");

    // Translate buttons
    if (acceptButton) {
      acceptButton.textContent =
        this.i18nManager.t("cookies.accept_all") || "Accept All";
    }
    if (customizeButton) {
      customizeButton.textContent =
        this.i18nManager.t("cookies.customize") || "Customize";
    }

    if (acceptButton) {
      // Remove existing listeners to avoid duplicates
      const newAcceptButton = acceptButton.cloneNode(true) as HTMLButtonElement;
      acceptButton.parentNode?.replaceChild(newAcceptButton, acceptButton);
      newAcceptButton.addEventListener("click", () => {
        this.acceptAll();
        banner.classList.remove("show");
        banner.classList.add("hidden");
      });
    }

    if (customizeButton) {
      // Remove existing listeners to avoid duplicates
      const newCustomizeButton = customizeButton.cloneNode(
        true
      ) as HTMLButtonElement;
      customizeButton.parentNode?.replaceChild(
        newCustomizeButton,
        customizeButton
      );
      newCustomizeButton.addEventListener("click", () => {
        this.showCustomizationDialog();
      });
    }
  }

  private setupPolicyLinks(): void {
    try {
      const privacyLink = document.getElementById("privacy-link");
      const cookieLink = document.getElementById("cookie-link");
      const termsLink = document.getElementById("terms-link");
      const accessibilityLink = document.getElementById("accessibility-link");

      if (privacyLink) {
        privacyLink.addEventListener("click", (e) => {
          e.preventDefault();
          this.showPolicyDialog("privacy");
        });
      }

      if (cookieLink) {
        cookieLink.addEventListener("click", (e) => {
          e.preventDefault();
          this.showPolicyDialog("cookie");
        });
      }

      if (termsLink) {
        termsLink.addEventListener("click", (e) => {
          e.preventDefault();
          this.showPolicyDialog("terms");
        });
      }

      if (accessibilityLink) {
        accessibilityLink.addEventListener("click", (e) => {
          e.preventDefault();
          this.showPolicyDialog("accessibility");
        });
      }
    } catch (error) {
      console.error("Error setting up policy links:", error);
    }
  }

  private acceptAll(): void {
    const consent: CookieConsent = {
      necessary: true,
      analytics: true,
      marketing: true,
      timestamp: Date.now(),
    };

    this.saveConsent(consent);
    this.applyConsent();
    this.trackEvent("cookie_consent", { action: "accept_all" });
  }

  // Public method to show customization dialog (can be called from anywhere)
  showCustomizationDialog(): void {
    // Hide cookie banner when showing customization dialog
    const banner = document.getElementById("cookie-banner");
    if (banner) {
      banner.classList.remove("show");
      banner.style.zIndex = "1"; // Ensure it's behind the dialog
      banner.style.pointerEvents = "none"; // Prevent interaction
    }

    // Create customization dialog
    const dialog = document.createElement("div");
    dialog.className = "cookie-customization-dialog";
    dialog.style.zIndex = "10000"; // Ensure it's above everything
    dialog.innerHTML = `
      <div class="dialog-backdrop"></div>
      <div class="dialog-content">
        <div class="dialog-header">
          <h2>${this.i18nManager.t("cookies.customize_title")}</h2>
          <button class="dialog-close" aria-label="Close dialog">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
        
        <div class="dialog-body">
          <p class="cookie-description">
            ${this.i18nManager.t("cookies.customize_description")}
          </p>
          
          <div class="cookie-options">
            <div class="cookie-option">
              <div class="cookie-option-header">
                <h3>${this.i18nManager.t("cookies.necessary")}</h3>
                <label class="cookie-toggle">
                  <input type="checkbox" id="necessary-cookies" checked disabled>
                  <span class="toggle-slider"></span>
                </label>
              </div>
              <p class="cookie-option-description">
                ${this.i18nManager.t("cookies.necessary_description")}
              </p>
            </div>
            
            <div class="cookie-option">
              <div class="cookie-option-header">
                <h3>${this.i18nManager.t("cookies.analytics")}</h3>
                <label class="cookie-toggle">
                  <input type="checkbox" id="analytics-cookies" ${
                    this.currentConsent?.analytics ? "checked" : ""
                  }>
                  <span class="toggle-slider"></span>
                </label>
              </div>
              <p class="cookie-option-description">
                ${this.i18nManager.t("cookies.analytics_description")}
              </p>
            </div>
            
            <div class="cookie-option">
              <div class="cookie-option-header">
                <h3>${this.i18nManager.t("cookies.marketing")}</h3>
                <label class="cookie-toggle">
                  <input type="checkbox" id="marketing-cookies" ${
                    this.currentConsent?.marketing ? "checked" : ""
                  }>
                  <span class="toggle-slider"></span>
                </label>
              </div>
              <p class="cookie-option-description">
                ${this.i18nManager.t("cookies.marketing_description")}
              </p>
            </div>
          </div>
        </div>
        
        <div class="dialog-footer">
          <button class="glass-button" id="save-cookie-preferences">
            ${this.i18nManager.t("cookies.save_preferences")}
          </button>
          <button class="glass-button glass-button--primary" id="accept-all-cookies">
            ${this.i18nManager.t("cookies.accept_all")}
          </button>
        </div>
      </div>
    `;

    document.body.appendChild(dialog);

    // Setup event listeners
    const closeButton = dialog.querySelector(".dialog-close");
    const backdrop = dialog.querySelector(".dialog-backdrop");
    const saveButton = dialog.querySelector("#save-cookie-preferences");
    const acceptAllButton = dialog.querySelector("#accept-all-cookies");

    const closeDialog = () => {
      document.body.removeChild(dialog);
      // Reset banner styles
      if (banner) {
        banner.style.zIndex = ""; // Reset z-index
        banner.style.pointerEvents = ""; // Re-enable interaction
        // Only show banner again if no consent was given
        if (!this.currentConsent) {
          banner.classList.remove("hidden");
          banner.classList.add("show");
        } else {
          // If consent was given, ensure banner is hidden
          banner.classList.remove("show");
          banner.classList.add("hidden");
        }
      }
    };

    closeButton?.addEventListener("click", closeDialog);
    backdrop?.addEventListener("click", closeDialog);

    saveButton?.addEventListener("click", () => {
      const necessary = true; // Always true
      const analytics =
        (dialog.querySelector("#analytics-cookies") as HTMLInputElement)
          ?.checked || false;
      const marketing =
        (dialog.querySelector("#marketing-cookies") as HTMLInputElement)
          ?.checked || false;

      const consent: CookieConsent = {
        necessary,
        analytics,
        marketing,
        timestamp: Date.now(),
      };

      this.saveConsent(consent);
      this.applyConsent();
      this.trackEvent("cookie_consent", {
        action: "customize",
        analytics,
        marketing,
      });
      closeDialog();
    });

    acceptAllButton?.addEventListener("click", () => {
      this.acceptAll();
      closeDialog();
    });

    // ESC key to close
    const handleKeydown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        closeDialog();
        document.removeEventListener("keydown", handleKeydown);
      }
    };
    document.addEventListener("keydown", handleKeydown);
  }

  private showPolicyDialog(
    type: "privacy" | "cookie" | "terms" | "accessibility"
  ): void {
    const policyDialog = document.getElementById("policy-dialog");
    const policyTitle = document.getElementById("policy-dialog-title");
    const policyContent = document.getElementById("policy-content");

    if (!policyDialog || !policyTitle || !policyContent) return;

    const policies = {
      privacy: {
        title: this.i18nManager.t("privacy_policy_title"),
        content: this.getPrivacyPolicyContent(),
      },
      cookie: {
        title: this.i18nManager.t("cookie_policy_title"),
        content: this.getCookiePolicyContent(),
      },
      terms: {
        title: this.i18nManager.t("terms_title"),
        content: this.getTermsContent(),
      },
      accessibility: {
        title: this.i18nManager.t("accessibility.title"),
        content: this.getAccessibilityContent(),
      },
    };

    policyTitle.textContent = policies[type].title;
    policyContent.innerHTML = policies[type].content;

    // Open dialog using DialogManager
    this.dialogManager.openDialog("policy-dialog");

    // Setup cookie preferences button if this is the cookie policy
    if (type === "cookie") {
      // Use setTimeout to ensure the button is in the DOM
      setTimeout(() => {
        const manageBtn = document.getElementById("cookie-policy-manage-btn");
        if (manageBtn) {
          manageBtn.addEventListener("click", () => {
            // Close the policy dialog first
            this.dialogManager.closeDialog();
            // Then open the customization dialog
            this.showCustomizationDialog();
          });
        }
      }, 100);
    }
  }

  private getPrivacyPolicyContent(): string {
    return `
      <div class="policy-content">
        <h3>${this.i18nManager.t("privacy.section1_title")}</h3>
        <p>${this.i18nManager.t("privacy.section1_content")}</p>
        
        <h3>${this.i18nManager.t("privacy.section2_title")}</h3>
        <p>${this.i18nManager.t("privacy.section2_content")}</p>
        
        <h3>${this.i18nManager.t("privacy.section3_title")}</h3>
        <p>${this.i18nManager.t("privacy.section3_content")}</p>
        
        <h3>${this.i18nManager.t("privacy.section4_title")}</h3>
        <p>${this.i18nManager.t("privacy.section4_content")}</p>
        
        <h3>${this.i18nManager.t("privacy.section5_title")}</h3>
        <p>${this.i18nManager.t("privacy.section5_content")}</p>
        
        <h3>${this.i18nManager.t("privacy.section6_title")}</h3>
        <p>${this.i18nManager.t("privacy.section6_content")}</p>
        
        <h3>${this.i18nManager.t("privacy.section7_title")}</h3>
        <p>${this.i18nManager.t("privacy.section7_content")}</p>
        
        <h3>${this.i18nManager.t("privacy.section8_title")}</h3>
        <p>${this.i18nManager.t("privacy.section8_content")}</p>
        
        <h3>${this.i18nManager.t("privacy.contact_title")}</h3>
        <p>${this.i18nManager.t("privacy.contact_content")}</p>
      </div>
    `;
  }

  private getCookiePolicyContent(): string {
    return `
      <div class="policy-content">
        <h3>${this.i18nManager.t("cookies.what_are_cookies")}</h3>
        <p>${this.i18nManager.t("cookies.what_are_cookies_content")}</p>
        
        <h3>${this.i18nManager.t("cookies.how_we_use_cookies")}</h3>
        <p>${this.i18nManager.t("cookies.how_we_use_cookies_content")}</p>
        
        <h3>${this.i18nManager.t("cookies.cookie_types")}</h3>
        <p>${this.i18nManager.t("cookies.cookie_types_content")}</p>
        <ul>
          <li><strong>${this.i18nManager.t(
            "cookies.necessary"
          )}:</strong> ${this.i18nManager.t("cookies.necessary_details")}</li>
          <li><strong>${this.i18nManager.t(
            "cookies.analytics"
          )}:</strong> ${this.i18nManager.t("cookies.analytics_details")}</li>
          <li><strong>${this.i18nManager.t(
            "cookies.marketing"
          )}:</strong> ${this.i18nManager.t("cookies.marketing_details")}</li>
        </ul>
        
        <h3>${this.i18nManager.t("cookies.third_party_title")}</h3>
        <p>${this.i18nManager.t("cookies.third_party_content")}</p>
        
        <h3>${this.i18nManager.t("cookies.retention_title")}</h3>
        <p>${this.i18nManager.t("cookies.retention_content")}</p>
        
        <h3>${this.i18nManager.t("cookies.manage_cookies")}</h3>
        <p>${this.i18nManager.t("cookies.manage_cookies_content")}</p>
        
        <div class="cookie-policy-actions" style="margin-top: var(--space-xl); padding-top: var(--space-lg); border-top: 1px solid var(--color-border);">
          <button class="glass-button glass-button--primary" id="cookie-policy-manage-btn" style="width: 100%;">
            ${
              this.i18nManager.t("cookies.manage_preferences") ||
              "Manage Cookie Preferences"
            }
          </button>
        </div>
      </div>
    `;
  }

  private getTermsContent(): string {
    return `
      <div class="policy-content">
        <h3>${this.i18nManager.t("terms.section1_title")}</h3>
        <p>${this.i18nManager.t("terms.section1_content")}</p>
        
        <h3>${this.i18nManager.t("terms.section2_title")}</h3>
        <p>${this.i18nManager.t("terms.section2_content")}</p>
        
        <h3>${this.i18nManager.t("terms.section3_title")}</h3>
        <p>${this.i18nManager.t("terms.section3_content")}</p>
        
        <h3>${this.i18nManager.t("terms.section4_title")}</h3>
        <p>${this.i18nManager.t("terms.section4_content")}</p>
        
        <h3>${this.i18nManager.t("terms.section5_title")}</h3>
        <p>${this.i18nManager.t("terms.section5_content")}</p>
        
        <h3>${this.i18nManager.t("terms.section6_title")}</h3>
        <p>${this.i18nManager.t("terms.section6_content")}</p>
        
        <h3>${this.i18nManager.t("terms.section7_title")}</h3>
        <p>${this.i18nManager.t("terms.section7_content")}</p>
        
        <h3>${this.i18nManager.t("terms.section8_title")}</h3>
        <p>${this.i18nManager.t("terms.section8_content")}</p>
        
        <h3>${this.i18nManager.t("terms.section9_title")}</h3>
        <p>${this.i18nManager.t("terms.section9_content")}</p>
        
        <h3>${this.i18nManager.t("terms.contact_title")}</h3>
        <p>${this.i18nManager.t("terms.contact_content")}</p>
      </div>
    `;
  }

  private getAccessibilityContent(): string {
    return `
      <div class="policy-content">
        <h3>${this.i18nManager.t("accessibility.section1_title")}</h3>
        <p>${this.i18nManager.t("accessibility.section1_content")}</p>
        
        <h3>${this.i18nManager.t("accessibility.section2_title")}</h3>
        <p>${this.i18nManager.t("accessibility.section2_content")}</p>
        
        <h3>${this.i18nManager.t("accessibility.section3_title")}</h3>
        <p>${this.i18nManager.t("accessibility.section3_content")}</p>
        
        <h3>${this.i18nManager.t("accessibility.section4_title")}</h3>
        <p>${this.i18nManager.t("accessibility.section4_content")}</p>
        
        <h3>${this.i18nManager.t("accessibility.section5_title")}</h3>
        <p>${this.i18nManager.t("accessibility.section5_content")}</p>
        
        <h3>${this.i18nManager.t("accessibility.section6_title")}</h3>
        <p>${this.i18nManager.t("accessibility.section6_content")}</p>
        
        <h3>${this.i18nManager.t("accessibility.section7_title")}</h3>
        <p>${this.i18nManager.t("accessibility.section7_content")}</p>
        
        <h3>${this.i18nManager.t("accessibility.section8_title")}</h3>
        <p>${this.i18nManager.t("accessibility.section8_content")}</p>
        
        <h3>${this.i18nManager.t("accessibility.section9_title")}</h3>
        <p>${this.i18nManager.t("accessibility.section9_content")}</p>
        
        <h3>${this.i18nManager.t("accessibility.contact_title")}</h3>
        <p>${this.i18nManager.t("accessibility.contact_content")}</p>
      </div>
    `;
  }

  private saveConsent(consent: CookieConsent): void {
    try {
      localStorage.setItem(this.consentKey, JSON.stringify(consent));
      this.currentConsent = consent;
    } catch (error) {
      console.error("Failed to save cookie consent:", error);
    }
  }

  private applyConsent(): void {
    if (!this.currentConsent) return;

    // Apply analytics consent
    if (this.currentConsent.analytics) {
      this.enableAnalytics();
    } else {
      this.disableAnalytics();
    }

    // Apply marketing consent
    if (this.currentConsent.marketing) {
      this.enableMarketing();
    } else {
      this.disableMarketing();
    }

    // Necessary cookies are always enabled
    this.enableNecessary();
  }

  private enableAnalytics(): void {
    // Enable Google Analytics or other analytics tools
    // Implement actual analytics initialization here
  }

  private disableAnalytics(): void {
    // Disable analytics tracking
    // Implement actual analytics cleanup here
  }

  private enableMarketing(): void {
    // Enable marketing cookies
    // Implement actual marketing initialization here
  }

  private disableMarketing(): void {
    // Disable marketing cookies
    // Implement actual marketing cleanup here
  }

  private enableNecessary(): void {
    // Necessary cookies are always enabled
    // No action needed
  }

  private trackEvent(eventName: string, parameters: Record<string, any>): void {
    // Only track if analytics consent is given
    if (this.currentConsent?.analytics) {
      // Implement actual tracking here (Google Analytics, etc.)
    }
  }

  // Public methods
  hasConsent(): boolean {
    return this.currentConsent !== null;
  }

  getConsent(): CookieConsent | null {
    return this.currentConsent;
  }

  updateConsent(consent: Partial<CookieConsent>): void {
    if (this.currentConsent) {
      const updatedConsent = { ...this.currentConsent, ...consent };
      this.saveConsent(updatedConsent);
      this.applyConsent();
    }
  }

  revokeConsent(): void {
    localStorage.removeItem(this.consentKey);
    this.currentConsent = null;
    this.disableAnalytics();
    this.disableMarketing();
    this.enableNecessary();
  }
}
